import React from "react"
import { useState, useRef, useMemo, useEffect, useCallback } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import { Check, ChevronsUpDown, Lock, AlertTriangle, Loader2, Plus, Undo2, X } from "lucide-react"
import { ChangedPill } from "./display-helpers"
import { useTranslation } from "@/hooks/use-translation"

/**
 * Primary form control for the onboarding drawer.
 *
 * Renders a labeled dropdown with optional search, async server-side filtering,
 * custom option rendering, and inline "create new" actions. When `creatingLabel`
 * is provided, the dropdown is replaced with a yellow "Will create" banner and
 * an undo button, giving a consistent creation UX across Worker and
 * Worker Organization fields.
 *
 * Supports: required/optional markers, source hint labels, changed pills, locked
 * state with badge, error/helper text, and a `labelAction` slot (e.g. unlock toggle).
 */

export interface SearchableSelectOption {
  id: string
  name: string
  canonicalName?: string
}

export interface SearchableSelectProps {
  label: string
  value: string | null
  onValueChange: (value: string) => void
  /** The list of options to display in the dropdown */
  options: SearchableSelectOption[]
  /** When provided, search is server-side: the component calls this instead of filtering options locally */
  onSearch?: (term: string) => void
  /** Loading indicator for server-side search */
  loading?: boolean
  /** Label for the currently selected value (for server-side mode where the selected item may not be in options) */
  selectedLabel?: string
  placeholder?: string
  disabled?: boolean
  locked?: boolean
  error?: string
  helperText?: string
  renderOption?: (option: SearchableSelectOption) => React.ReactNode
  required?: boolean
  atsHint?: React.ReactNode
  changedPill?: boolean
  /** Content rendered after the options list inside the dropdown (e.g. creation actions) */
  children?: React.ReactNode
  /** When provided, shows a "Create new" action when search term doesn't match any existing option */
  onCreateNew?: (name: string) => void
  /** Label prefix for the create action (default: "Create new") */
  createNewLabel?: string
  /** When true, suppresses both the suggested and search-based "Create new" actions even if `onCreateNew` is set. Useful when the parent has business reasons to disallow creation (e.g. selected entity already has an associated record). */
  disableCreateNew?: boolean
  /** When true, the entire label row (label + atsHint + changedPill + locked badge + labelAction) is hidden. Use when the parent renders its own label outside this control. */
  hideLabel?: boolean
  /** Node rendered at the far right of the label row (e.g. lock toggle) */
  labelAction?: React.ReactNode
  /** When true, hides the search input (useful for small option lists) */
  hideSearch?: boolean
  /** When set, replaces the input with a yellow "Will create" banner */
  creatingLabel?: string
  /** Called when the user clicks undo on the creation banner */
  onUndoCreate?: () => void
  /** Warning shown below the creation banner (e.g. duplicate awareness) */
  creatingWarning?: string
  /** Pre-suggested name for the "Create new" action (e.g. from source data), shown as a pinned action */
  suggestedCreateName?: string
  /** Short label for the data source (e.g. "ATS", "CR"), used in hint text */
  sourceLabel?: string
  /** Called when the user clicks the clear button; when provided, shows a clear button inside the trigger when a value is selected */
  onClear?: () => void
  /** When true, intercept the "Create new" action with an inline "Are you sure?" prompt whenever matching options are visible above. Prevents accidental creation of duplicate Worker Organizations when the user typed a query that DOES return results but didn't pick any of them. */
  confirmCreateWhenMatchesVisible?: boolean
}

const DEBOUNCE_MS = 300

export function SearchableSelect({
  label,
  value,
  onValueChange,
  options,
  onSearch,
  loading = false,
  selectedLabel: selectedLabelProp,
  placeholder,
  disabled,
  locked,
  error,
  helperText,
  renderOption,
  required,
  atsHint,
  changedPill,
  children,
  onCreateNew,
  createNewLabel,
  disableCreateNew = false,
  hideLabel = false,
  labelAction,
  hideSearch,
  creatingLabel,
  onUndoCreate,
  creatingWarning,
  suggestedCreateName,
  sourceLabel,
  onClear,
  confirmCreateWhenMatchesVisible = false,
}: SearchableSelectProps) {
  const { t } = useTranslation()
  const effectiveCreateNewLabel = createNewLabel ?? t("createNew")
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")
  // Single-step "Create new" gate: when matches are visible the click first surfaces an
  // "Are you sure?" panel (string holds the proposed name), then "Yes, continue" commits via
  // onCreateNew and closes the popover. The actual name-editing happens at the FORM level
  // (the parent renders an embedded editor when workerOrgScenario.kind === "new"), so the
  // popover stays focused on its core job: search + intercepted creation.
  const [pendingCreateName, setPendingCreateName] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const prevCreatingRef = useRef(creatingLabel)

  // Close popover when transitioning out of "creating" mode (e.g. after corrections
  // switch a create-contact back to existing-profile). The popover's `open` state
  // can be stale-true from the interaction that triggered creation.
  // Single close path — every dismissal needs to clear pendingCreateName, otherwise the
  // confirmation panel rehydrates the next time the popover opens (a click-outside while the
  // panel was visible would otherwise leave the gate stuck on a stale name).
  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next)
    if (!next) setPendingCreateName(null)
  }, [])

  useEffect(() => {
    if (prevCreatingRef.current && !creatingLabel) {
      handleOpenChange(false)
    }
    prevCreatingRef.current = creatingLabel
  }, [creatingLabel, handleOpenChange])

  const isAsync = !!onSearch

  // Client-side filtering when no onSearch is provided
  const filtered = useMemo(() => {
    if (isAsync) return options
    if (!search) return options
    const lower = search.toLowerCase()
    return options.filter((o) => o.name.toLowerCase().includes(lower))
  }, [options, search, isAsync])

  // Debounced server-side search
  const handleSearchChange = useCallback(
    (term: string) => {
      setSearch(term)
      // Editing the search invalidates any in-flight confirmation — the user may have changed
      // their mind about what to create, or be narrowing the search to find a real match.
      setPendingCreateName(null)
      if (!isAsync) return
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        // Trim before hitting the index — Azure Search treats trailing whitespace as part of
        // the query and returns 0 hits when there are real matches. Without this, "Stark
        // industries " (note trailing space) shows "No results found" and the user can fall
        // through to the create-new path with a name that already exists.
        onSearch(term.trim())
      }, DEBOUNCE_MS)
    },
    [isAsync, onSearch]
  )

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  // Trigger search when popover opens in async mode,
  // or on mount when a value is pre-populated but has no label yet
  useEffect(() => {
    if (open && isAsync) {
      onSearch(search.trim())
    }
  }, [open])

  // Refetch options when the selected value changes and isn't already represented in the
  // dropdown's option list. Without this, switching between two existing-profile contacts
  // whose worker orgs differ leaves the trigger blank — the first value's options were
  // fetched once and never refreshed for the second value, so its org name can't render.
  // The ref records which value we actually FETCHED for, not what we last saw. That keeps
  // a future options change (e.g. value dropped from the list) able to retrigger a fetch,
  // and still prevents looping when the server's response legitimately omits the value.
  const fetchedForValueRef = useRef<string | null>(null)
  useEffect(() => {
    if (!isAsync) return
    // Reset on clear so re-selecting the same id later still refires the fetch.
    if (!value) {
      fetchedForValueRef.current = null
      return
    }
    if (selectedLabelProp) return
    if (fetchedForValueRef.current === value) return
    if (options.some((o) => o.id === value)) return
    fetchedForValueRef.current = value
    onSearch("")
  }, [isAsync, value, selectedLabelProp, options, onSearch])

  // Resolve selected label: explicit prop (e.g. new org name) takes priority,
  // then fall back to matching the value against options
  const selectedLabel = selectedLabelProp
    ?? (value ? (options.find((o) => o.id === value)?.name ?? null) : null)

  const isLocked = locked || false

  // Match against canonicalName when present (e.g. organizations whose `name` is rendered as
  // "Brightvale Financial Inc (BRIGHT)" but whose canonicalName is the bare "Brightvale Financial Inc").
  // Without this, a typed query like "shaheen inc" never compares equal to the option label and
  // the "Create new" affordance stays visible even when an exact match already exists.
  const matchesAnyOption = useCallback(
    (term: string) => {
      const normalized = term.trim().toLowerCase()
      if (!normalized) return false
      return options.some((o) => {
        const display = o.name.trim().toLowerCase()
        const canonical = o.canonicalName?.trim().toLowerCase()
        return display === normalized || canonical === normalized
      })
    },
    [options]
  )

  // Show suggested create action when the source-provided name doesn't match any existing option
  const showSuggestedCreate =
    !!onCreateNew &&
    !disableCreateNew &&
    !!suggestedCreateName &&
    !matchesAnyOption(suggestedCreateName)

  // Show search-based create action when the typed search doesn't match any option
  // and isn't the same as the suggested name (to avoid duplicate actions)
  const showCreateAction =
    !!onCreateNew &&
    !disableCreateNew &&
    search.trim().length > 0 &&
    !matchesAnyOption(search) &&
    !(showSuggestedCreate && search.trim().toLowerCase() === suggestedCreateName?.trim().toLowerCase())

  // When confirmation is required AND a visible option could plausibly be the duplicate the
  // user is about to create, route the create click through the pending state so they see an
  // "Are you sure?" panel before commit. We match against the *candidate* name, not the typed
  // search — in async mode `filtered === options` regardless of search, and the
  // suggested-create flow can fire with a candidate name that has nothing to do with what the
  // user typed. Counting only candidate-name substring hits avoids over-firing in both cases.
  const requestCreate = useCallback(
    (name: string) => {
      // Block creation while an async search is in flight — the duplicate-detection below
      // reads the current `filtered` snapshot, which is stale until the latest search
      // resolves. The render-side gate hides the create CTAs during loading so this is
      // unreachable through normal interaction; this is the belt-and-braces guard for any
      // future caller.
      if (loading) return
      if (confirmCreateWhenMatchesVisible) {
        const lower = name.trim().toLowerCase()
        const hasNameMatch =
          lower.length > 0 &&
          filtered.some((o) => {
            const display = o.name.toLowerCase()
            const canonical = o.canonicalName?.toLowerCase()
            return display.includes(lower) || (canonical?.includes(lower) ?? false)
          })
        if (hasNameMatch) {
          setPendingCreateName(name)
          return
        }
      }
      onCreateNew?.(name)
      handleOpenChange(false)
      setSearch("")
    },
    [loading, confirmCreateWhenMatchesVisible, filtered, onCreateNew, handleOpenChange]
  )

  const cancelCreation = useCallback(() => {
    setPendingCreateName(null)
  }, [])

  const commitCreate = useCallback(
    (name: string) => {
      onCreateNew?.(name)
      handleOpenChange(false)
      setSearch("")
    },
    [onCreateNew, handleOpenChange]
  )

  return (
    <div className="flex flex-col gap-1.5">
      {!hideLabel && (
        <div className="flex items-center gap-2 flex-wrap">
          <Label className="text-sm font-medium text-gray-700">
            {label}
            {required && <span className="text-red-500 ml-0.5">*</span>}
          </Label>
          {atsHint}
          {changedPill && <ChangedPill />}
          {isLocked && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
              <Lock size={10} />
              {t("locked")}
            </span>
          )}
          {labelAction && <span className="ml-auto">{labelAction}</span>}
        </div>
      )}
      {creatingLabel ? (
        <>
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-yellow-50 border border-yellow-300">
            <span className="text-xs text-yellow-800 flex-1">
              {t("willCreate")}{" "}
              <span className="font-medium">{creatingLabel}</span>
            </span>
            {onUndoCreate && (
              <button
                type="button"
                onClick={onUndoCreate}
                className="text-yellow-600 hover:text-yellow-800 transition-colors"
                aria-label={t("undoCreation")}
              >
                <Undo2 size={14} />
              </button>
            )}
          </div>
          {creatingWarning && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-yellow-50 border border-yellow-200">
              <AlertTriangle size={14} className="text-yellow-600 shrink-0" />
              <p className="text-xs text-yellow-700">{creatingWarning}</p>
            </div>
          )}
        </>
      ) : (
      <Popover open={open} onOpenChange={isLocked ? undefined : handleOpenChange}>
        <div className="relative">
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              disabled={disabled || isLocked}
              className={cn(
                "w-full bg-transparent font-normal h-9 text-sm",
                onClear && value ? "justify-start pr-8" : "justify-between",
                !selectedLabel && "text-gray-400",
                error && "border-red-300 focus:ring-red-500",
                isLocked && "bg-gray-50 opacity-70 cursor-not-allowed"
              )}
            >
              <span className="truncate">{selectedLabel ?? placeholder ?? t("selectEllipsis")}</span>
              {(!onClear || !value) && <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />}
            </Button>
          </PopoverTrigger>
          {onClear && value && !disabled && !isLocked && (
            <button
              type="button"
              onClick={() => {
                onClear()
                setSearch("")
                handleOpenChange(false)
              }}
              aria-label={t("clearSelection")}
              className="absolute right-1 top-1 h-7 w-7 inline-flex items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          {!hideSearch && (
            <div className="p-2 border-b">
              <Input
                ref={inputRef}
                placeholder={onCreateNew ? t("placeholderSearchOrCreate") : isAsync ? t("placeholderTypeToSearch") : t("placeholderSearch")}
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="h-8 text-sm"
              />
            </div>
          )}
          <div className="max-h-[260px] overflow-y-auto p-1">
            {showSuggestedCreate && !pendingCreateName && !loading && (
              <>
                <button
                  type="button"
                  onClick={() => requestCreate(suggestedCreateName!.trim())}
                  className="w-full text-left px-2 py-2 rounded-md text-sm flex items-center gap-2 hover:bg-blue-50 transition-colors text-blue-700"
                >
                  <Plus size={14} className="shrink-0" />
                  <span>
                    <span className="font-medium">{t("createNewLabeled", { label: effectiveCreateNewLabel, name: suggestedCreateName!.trim() })}</span>
                    {" "}
                    {sourceLabel && <span className="text-[10px] text-gray-400">{t("fromSourceLabel", { source: sourceLabel })}</span>}
                  </span>
                </button>
                <div className="border-t border-gray-100 my-1" />
              </>
            )}
            {loading ? (
              <div className="py-4 flex items-center justify-center gap-2 text-sm text-gray-500">
                <Loader2 size={14} className="animate-spin" />
                {t("searching")}
              </div>
            ) : filtered.length === 0 && !children && !showSuggestedCreate ? (
              <div className="py-4 text-center text-sm text-gray-500">
                {isAsync && !search ? t("placeholderTypeToSearch") : t("noResultsFound")}
              </div>
            ) : (
              <>
                {filtered.map((option) => (
                  <button
                    type="button"
                    key={option.id}
                    onClick={() => {
                      onValueChange(option.id)
                      handleOpenChange(false)
                      setSearch("")
                    }}
                    className={cn(
                      "w-full text-left px-2 py-1.5 rounded-md text-sm flex items-center gap-2 hover:bg-gray-100 transition-colors",
                      value === option.id && "bg-gray-50"
                    )}
                  >
                    <Check
                      className={cn(
                        "h-4 w-4 shrink-0",
                        value === option.id ? "opacity-100 text-orange-600" : "opacity-0"
                      )}
                    />
                    {renderOption ? renderOption(option) : <span>{option.name}</span>}
                  </button>
                ))}
                {children}
              </>
            )}
            {showCreateAction && !pendingCreateName && !loading && (
              <>
                <div className="border-t border-gray-100 my-1" />
                <button
                  type="button"
                  onClick={() => requestCreate(search.trim())}
                  className="w-full text-left px-2 py-2 rounded-md text-sm flex items-center gap-2 hover:bg-blue-50 transition-colors text-blue-700"
                >
                  <Plus size={14} className="shrink-0" />
                  <span className="font-medium">
                    {t("createNewLabeled", { label: effectiveCreateNewLabel, name: search.trim() })}
                  </span>
                </button>
              </>
            )}
            {pendingCreateName && (
              <>
                <div className="border-t border-gray-100 my-1" />
                <div className="m-1 p-3 rounded-md bg-yellow-50 border border-yellow-300">
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={14} className="text-yellow-600 shrink-0 mt-0.5" />
                    <div className="flex-1 flex flex-col gap-2">
                      <div className="flex flex-col gap-0.5">
                        <p className="text-xs font-medium text-yellow-900">
                          {t("possibleMatchesAbove", { count: filtered.length, plural: filtered.length === 1 ? "" : "es" })}
                        </p>
                        <p className="text-xs text-yellow-800 leading-relaxed">
                          {t("continueCreatingNewEntry")}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => commitCreate(pendingCreateName)}
                          className="text-xs font-medium px-2.5 py-1.5 rounded bg-orange-600 text-white hover:bg-orange-700 transition-colors"
                        >
                          {t("yesContinue")}
                        </button>
                        <button
                          type="button"
                          onClick={cancelCreation}
                          className="text-xs font-medium px-2.5 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          {t("cancel")}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </PopoverContent>
      </Popover>
      )}
      <p className={cn("text-xs min-h-4", error ? "text-red-500" : "text-gray-400")}>
        {error ?? helperText}
      </p>
    </div>
  )
}
